// Pure helpers for the prep editor: the html sanitizer, the keyboard decisions
// for bullet lists, ids, and the layout and live-merge arithmetic. No DOM in
// here, so `node --test` runs them (packages/core/test/prepEdit.test.mjs).

export interface Sec {
  readonly id: string;
  readonly title: string;
  readonly html: string;
  /** Starts closed; set by the server, never saved from here. */
  readonly fold?: boolean;
}

// ---- html ----------------------------------------------------------------------

const ALLOWED = new Set(["ul", "li", "b", "mark", "p", "br", "table", "thead", "tbody", "tr", "th", "td"]);
/** The parts of a table that hold rows or cells, never text of their own. */
const FRAME = new Set(["table", "thead", "tbody", "tr"]);
const RENAMED: Record<string, string> = { strong: "b", div: "p", ol: "ul" };
const BLOCKS = new Set(["ul", "p"]);
/** Tags whose whole content goes with the tag. */
const DROPPED = new Set(["script", "style", "head", "title", "template", "iframe", "object", "noscript"]);
const INLINE = new Set(["b", "mark", "br"]);

interface Open {
  readonly tag: string;
  /** False for a tag that is kept off the page (a nested bold, a p in a li). */
  readonly emit: boolean;
}

/** Reduces editor html to ul, li (a nested ul inside an li), b, mark, p, br,
 * and tables, with every attribute removed. A table holds rows of th or td
 * cells, optionally grouped in a thead and tbody; a cell holds inline text, and
 * a paragraph or bullet inside one is kept as a line break. Safe on arbitrary input: unknown tags vanish
 * and keep their text, unbalanced tags are closed, text outside a block is
 * wrapped in a p, and an empty bullet reads as `<li></li>`. */
export function sanitizeHtml(input: string): string {
  const src = input.replace(/<!--[\s\S]*?-->/g, "");
  const out: string[] = [];
  const stack: Open[] = [];
  let skipUntil: string | null = null;

  const has = (tag: string): boolean => stack.some((o) => o.tag === tag);
  const top = (): Open | undefined => stack[stack.length - 1];
  const inCell = (): boolean => has("td") || has("th");
  const inBlockOrItem = (): boolean => has("p") || has("li") || inCell();
  /** Inside a table but outside any cell, where text has no place. */
  const betweenCells = (): boolean => FRAME.has(top()?.tag ?? "");
  const lastOf = (...tags: string[]): number => {
    for (let k = stack.length - 1; k >= 0; k--) if (tags.includes(stack[k]?.tag ?? "")) return k;
    return -1;
  };
  const push = (tag: string, emit: boolean): void => {
    stack.push({ tag, emit });
    if (emit) out.push(`<${tag}>`);
  };
  const closeTo = (index: number): void => {
    while (stack.length > index) {
      const o = stack.pop() as Open;
      if (o.emit) out.push(`</${o.tag}>`);
    }
  };
  const closeP = (): void => {
    // A paragraph holding block content ends where the block starts.
    const i = stack.map((o) => o.tag).lastIndexOf("p");
    if (i >= 0 && stack.slice(i + 1).every((o) => INLINE.has(o.tag))) closeTo(i);
  };
  const ensureBlock = (): void => {
    if (!inBlockOrItem() && !has("ul")) push("p", true);
    else if (top()?.tag === "ul") {
      // Text or inline content straight inside a list: give it a bullet.
      push("li", true);
    }
  };

  const re = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b[^>]*?(\/?)>|([^<]+)|</g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    if (m[4] !== undefined) {
      if (skipUntil) continue;
      const text = m[4].replace(/&nbsp;| /g, " ");
      if (text.trim() === "" && !inBlockOrItem()) continue;
      if (betweenCells()) continue;
      ensureBlock();
      out.push(text);
      continue;
    }
    if (m[2] === undefined) {
      // A lone "<" that opens nothing.
      if (!skipUntil && !betweenCells()) {
        ensureBlock();
        out.push("&lt;");
      }
      continue;
    }
    const closing = m[1] === "/";
    let tag = m[2].toLowerCase();
    if (skipUntil) {
      if (closing && tag === skipUntil) skipUntil = null;
      continue;
    }
    if (DROPPED.has(tag)) {
      if (!closing && m[3] !== "/") skipUntil = tag;
      continue;
    }
    tag = RENAMED[tag] ?? tag;
    if (!ALLOWED.has(tag)) continue;

    if (closing) {
      if (tag === "br") continue;
      let i = -1;
      for (let k = stack.length - 1; k >= 0; k--) {
        if (stack[k]?.tag === tag) {
          i = k;
          break;
        }
      }
      if (i < 0) continue;
      closeTo(i);
      continue;
    }

    if (tag === "table") {
      // A table stands on its own: it ends any paragraph or list around it,
      // and a table inside a table keeps only its text.
      if (has("table")) continue;
      closeTo(0);
      push("table", true);
      continue;
    }
    if (FRAME.has(tag) || tag === "td" || tag === "th") {
      const t = lastOf("table");
      if (t < 0) continue;
      // Where a row goes: inside the thead or tbody if one is open.
      const group = Math.max(t, lastOf("thead", "tbody"));
      if (tag === "thead" || tag === "tbody") {
        closeTo(t + 1);
        push(tag, true);
        continue;
      }
      if (tag === "tr") {
        closeTo(group + 1);
        push("tr", true);
        continue;
      }
      // A cell closes the cell beside it, and lives in a row.
      const row = lastOf("tr");
      if (row < t) {
        closeTo(group + 1);
        push("tr", true);
      } else closeTo(row + 1);
      push(tag, true);
      continue;
    }
    if (betweenCells()) continue;

    if (tag === "br") {
      ensureBlock();
      out.push("<br>");
      continue;
    }
    if ((tag === "li" || tag === "ul") && inCell()) {
      // A bullet in a cell is kept as a line of the cell.
      const last = out[out.length - 1] ?? "";
      if (tag === "li" && last !== "" && !/<t[dh]>$/.test(last) && !last.endsWith("<br>")) out.push("<br>");
      continue;
    }
    if (tag === "li") {
      // A bullet closes any bullet it is a sibling of, and lives in a ul.
      const li = stack.map((o) => o.tag).lastIndexOf("li");
      const ul = stack.map((o) => o.tag).lastIndexOf("ul");
      if (li >= 0 && li > ul) closeTo(li);
      if (top()?.tag !== "ul") {
        closeP();
        push("ul", true);
      }
      push("li", true);
      continue;
    }
    if (tag === "ul") {
      closeP();
      // A list straight inside a list hangs off the bullet before it.
      if (top()?.tag === "ul") push("li", true);
      push("ul", true);
      continue;
    }
    if (tag === "p") {
      if (has("li") || inCell()) {
        // Block content inside a bullet is kept as line breaks.
        const last = out[out.length - 1] ?? "";
        if (last !== "" && !last.endsWith("<li>") && !last.endsWith("<br>")) out.push("<br>");
        push("p", false);
        continue;
      }
      closeP();
      if (top()?.tag === "ul") closeTo(stack.length - 1);
      push("p", true);
      continue;
    }
    // b and mark
    ensureBlock();
    push(tag, !has(tag));
  }
  closeTo(0);

  let html = out.join("");
  // Empty things go: a bold with nothing in it, a bullet holding only a break.
  for (let i = 0; i < 4; i++) {
    const before = html;
    html = html
      .replace(/<(b|mark)><\/\1>/g, "")
      .replace(/<(b|mark)>(<br>)+<\/\1>/g, "<br>")
      .replace(/<\/b><b>/g, "")
      .replace(/<\/mark><mark>/g, "")
      .replace(/<li>(<br>)+<\/li>/g, "<li></li>")
      .replace(/(<br>)+<\/(li|p|td|th)>/g, "</$2>")
      .replace(/<(td|th)>(<br>)+/g, "<$1>")
      .replace(/<p><\/p>/g, "")
      .replace(/<ul><\/ul>/g, "")
      .replace(/<(thead|tbody|tr)><\/\1>/g, "")
      .replace(/<table><\/table>/g, "");
    if (html === before) break;
  }
  return html.trim();
}

/** What goes into the contentEditable: an empty bullet needs a break inside
 * it for the caret to have somewhere to stand. */
export function toEditable(html: string): string {
  return html.replace(/<li><\/li>/g, "<li><br></li>");
}

/** Plain text escaped for html. */
export function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Pasted text as bullets: one per non-empty line. */
export function linesToBullets(text: string): string {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  return lines.length ? `<ul>${lines.map((l) => `<li>${escapeHtml(l)}</li>`).join("")}</ul>` : "";
}

/** The visible text of some html, for the empty check and the previews. */
export function htmlText(html: string): string {
  return html
    .replace(/<br>/g, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .trim();
}

// ---- ids -----------------------------------------------------------------------

export const NEW_SECTION_HTML = "<ul><li></li></ul>";
export const NEW_SECTION_TITLE = "New section";

/** A new section id: `s-` and six base36 characters, which is the shape the
 * server accepts. */
export function newSectionId(random: () => number = Math.random): string {
  let id = "s-";
  for (let i = 0; i < 6; i++) id += Math.floor(random() * 36).toString(36);
  return id;
}

/** An id not already in use. */
export function freshSectionId(taken: ReadonlySet<string>, random: () => number = Math.random): string {
  for (let i = 0; i < 20; i++) {
    const id = newSectionId(random);
    if (!taken.has(id)) return id;
  }
  return `s-${Date.now().toString(36).slice(-6)}`;
}

// ---- keyboard decisions ----------------------------------------------------------

export interface BulletContext {
  /** The bullet sits inside another bullet's list. */
  readonly nested: boolean;
  /** It has no text and no nested bullets of its own. */
  readonly empty: boolean;
  /** There is a bullet before it in its own list. */
  readonly hasPrev: boolean;
}

export type BackspaceAction = "outdent" | "remove" | "merge" | "none";

/** Backspace with the caret at the start of a bullet. It never drops out of
 * the list into a paragraph. */
export function backspaceAtStart(c: BulletContext): BackspaceAction {
  if (c.nested) return "outdent";
  if (!c.hasPrev) return "none";
  return c.empty ? "remove" : "merge";
}

export type EnterAction = "outdent" | "none" | "default";

/** Enter in a bullet: an empty nested bullet steps out, an empty top-level one
 * stays put (the browser would leave the list), anything else is the browser's. */
export function enterInBullet(c: BulletContext): EnterAction {
  if (!c.empty) return "default";
  return c.nested ? "outdent" : "none";
}

export type TabAction = "indent" | "outdent" | "none";

export function tabInBullet(c: BulletContext, shift: boolean): TabAction {
  if (shift) return c.nested ? "outdent" : "none";
  return c.hasPrev ? "indent" : "none";
}

// ---- layout ------------------------------------------------------------------------

/** `order` with `id` moved to `index` (counted without it), or the same array
 * when nothing changes. */
export function moveId(order: readonly string[], id: string, index: number): string[] {
  const from = order.indexOf(id);
  if (from < 0) return [...order];
  const rest = order.filter((x) => x !== id);
  const at = Math.max(0, Math.min(rest.length, index));
  rest.splice(at, 0, id);
  return rest;
}

export function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Where a dragged section lands: its index among the others, from the pointer
 * height and the other sections' heights laid end to end from `top`. */
export function dropIndex(pointerY: number, top: number, heights: readonly number[]): number {
  let pos = top;
  for (let i = 0; i < heights.length; i++) {
    const h = heights[i] ?? 0;
    if (pointerY < pos + h / 2) return i;
    pos += h;
  }
  return heights.length;
}

// ---- live merge ----------------------------------------------------------------------

export interface MergeInput {
  readonly local: readonly Sec[];
  readonly remote: readonly Sec[];
  /** A section being edited, or with a save pending or just finished. */
  readonly busy: (id: string) => boolean;
  /** A drag or an order save is in flight: keep the local order. */
  readonly keepOrder: boolean;
  /** Ids deleted here, which a server copy older than the delete may still list. */
  readonly skip?: ReadonlySet<string>;
}

export interface MergeResult {
  readonly sections: readonly Sec[];
  /** Ids whose title or html came from the server. */
  readonly taken: readonly string[];
  readonly changed: boolean;
}

/** Folds the server's copy into the editor's. A section that is busy keeps its
 * own text; every other section takes the server's title and html. New
 * sections arrive, removed ones go, and the order follows the server unless it
 * is being changed here. */
export function mergeRemote({ local, remote: remoteAll, busy, keepOrder, skip }: MergeInput): MergeResult {
  const remote = skip && skip.size > 0 ? remoteAll.filter((s) => !skip.has(s.id)) : remoteAll;
  const localById = new Map(local.map((s) => [s.id, s]));
  const remoteIds = new Set(remote.map((s) => s.id));
  const taken: string[] = [];

  const pick = (r: Sec): Sec => {
    const l = localById.get(r.id);
    if (!l) {
      taken.push(r.id);
      return r;
    }
    if (busy(r.id)) return l;
    if (l.title !== r.title || l.html !== r.html) taken.push(r.id);
    return r.fold ? { id: r.id, title: r.title, html: r.html, fold: true } : { id: r.id, title: r.title, html: r.html };
  };

  // A section that exists only here and is busy is one just added.
  const keptLocalOnly = local.filter((s) => !remoteIds.has(s.id) && busy(s.id));
  let next: Sec[];
  if (keepOrder) {
    const out: Sec[] = [];
    for (const l of local) {
      const r = remote.find((x) => x.id === l.id);
      if (r) out.push(pick(r));
      else if (busy(l.id)) out.push(l);
    }
    for (const r of remote) if (!localById.has(r.id)) out.push(pick(r));
    next = out;
  } else {
    next = remote.map(pick);
    for (const l of keptLocalOnly) {
      const at = Math.min(local.indexOf(l), next.length);
      next.splice(at, 0, l);
    }
  }
  const changed =
    next.length !== local.length ||
    next.some((s, i) => {
      const l = local[i];
      return !l || s.id !== l.id || s.title !== l.title || s.html !== l.html || !!s.fold !== !!l.fold;
    });
  return { sections: changed ? next : local, taken, changed };
}

/** The headings that differ from what the server has. */
export function changedTitles(base: ReadonlyMap<string, { title: string }>, current: ReadonlyMap<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [id, title] of current) if (base.get(id)?.title !== title) out[id] = title;
  return out;
}

/** The slugs a chat turn changed that include the open one. */
export function touchesSlug(changed: readonly string[], slug: string): boolean {
  return changed.includes(slug);
}
